import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
const PAGE_SIZE = 50;

const stockOutcome = (notification) => notification.type !== 'Stock-New' ? null
  : String(notification.rejected ?? '') === '1' ? 'rejected'
  : notification.stockItems != null ? 'approved' : null;
const isNew = (notification) => !stockOutcome(notification) && (notification.status
  ? String(notification.status).toLowerCase() === 'new'
  : notification.unread !== false);
const isPending = (notification) => !stockOutcome(notification)
  && String(notification.status || '').toLowerCase() === 'read';
const isStockNotification = (notification) => notification.type === 'Stock-New';

const titleFor = (notification) => {
  if (notification.type === 'Stock-New') return notification.title || 'Stock Update Request';
  if (notification.type === 'pc-del') return `PC Delete Request - Indent #${notification.indent}`;
  if (notification.type === 'pc-dtd') return `PC Deleted - Indent #${notification.indent}`;
  if (notification.type === 'Ind-Rej') return `Indent Rejected - #${notification.indent}`;
  if (notification.type === 'GRN-Req') return `GRN Cancel Request - Indent #${notification.indent}`;
  if (notification.type === 'Ord-Rev') return `Order Reversal Request - Indent #${notification.indent}`;
  return notification.title || 'Notification';
};

const messageFor = (notification) => notification.remarks || notification.message || notification.description || '';

const formatTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(date);
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadNotifications = useCallback(async (offset = 0) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    setError('');
    try {
      const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      const response = await fetch(`${API_BASE_URL}/notifications/all?${query}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      const result = await response.json();
      if (!response.ok || result.error || !Array.isArray(result.data)) {
        throw new Error(result.message || 'Unable to load notifications');
      }
      setNotifications((current) => offset === 0 ? result.data : [...current, ...result.data]);
      setHasMore(result.data.length === PAGE_SIZE);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const visible = useMemo(() => notifications.filter((notification) => {
    if (filter === 'new' && !isNew(notification)) return false;
    if (filter === 'pending' && !isPending(notification)) return false;
    const text = `${notification.branch || ''} ${titleFor(notification)} ${messageFor(notification)} ${notification.status || ''}`.toLowerCase();
    return text.includes(search.trim().toLowerCase());
  }), [notifications, filter, search]);
  const newCount = notifications.filter(isNew).length;
  const pendingCount = notifications.filter(isPending).length;

  const markRead = async (notification) => {
    if (!isNew(notification) || notification.type === 'GRN-Req') return;
    try {
      const response = await fetch(`${API_BASE_URL}/notifications/${notification.id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (!response.ok) throw new Error('Unable to mark notification as read');
      await loadNotifications();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const openNotification = async (notification) => {
    if (!isNew(notification) && !isStockNotification(notification)) return;
    const type = notification.type;
    if (type === 'GRN-Req') {
      navigate(`/grn-cancel-request/${notification.indent}/${notification.id}/${encodeURIComponent(notification.remarks || '')}`);
      return;
    }
    if (isNew(notification) && ['Stock-New', 'pc-del', 'Ind-Rej'].includes(type)
      && !(type === 'Stock-New' && (notification.rejected === 1 || notification.stockItems != null))) {
      fetch(`${API_BASE_URL}/notifications/${notification.id}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      }).catch((requestError) => console.error('Unable to mark notification as read:', requestError));
    }
    if (type === 'Stock-New') {
      navigate('/stock-update', { state: { selectedNotification: notification, showOnlyThis: true } });
    } else if (type === 'pc-del') {
      navigate('/delete-PC', { state: { indentId: notification.indent, remarks: notification.remarks, notificationId: notification.id } });
    } else if (type === 'Ind-Rej') {
      navigate('/indent', { state: { rejectedIndentId: notification.indent, showRejectedIndent: true } });
    } else {
      await markRead(notification);
    }
  };

  return (
    <div className="mx-auto max-w-6xl rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="rounded-xl bg-blue-50 p-2 text-blue-600"><Bell size={24} /></span>
          <div><h1 className="text-2xl font-semibold text-slate-900">Notifications</h1><p className="text-sm text-slate-500">Click a notification to view its details.</p></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => loadNotifications()} disabled={loading || loadingMore} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"><RefreshCw size={16} /> Refresh</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 p-1">
          {[
            ['all', 'All'], ['new', 'New'], ['pending', 'Pending'],
          ].map(([value, label]) => (
            <button key={value} type="button" onClick={() => setFilter(value)} className={`relative rounded-md px-3 py-1.5 text-sm ${filter === value ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{label}{value !== 'all' && <span className={`ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold ${value === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{value === 'new' ? newCount : pendingCount}</span>}</button>
          ))}
        </div>
        <label className="ml-auto flex w-full items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-slate-500 sm:w-80"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search notifications" className="w-full bg-transparent text-sm text-slate-800 outline-none" /></label>
      </div>

      {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading ? <p className="py-12 text-center text-slate-500">Loading notifications...</p> : visible.length === 0 ? <p className="py-12 text-center text-slate-500">No notifications found.</p> : (
        <div className="mt-5 space-y-3">
          {visible.map((notification, index) => {
            const outcome = stockOutcome(notification);
            const status = String(notification.status || '').toLowerCase() === 'read'
              ? 'Pending' : notification.status || (isNew(notification) ? 'New' : 'Pending');
            const actionable = isStockNotification(notification) || isNew(notification);
            const ribbon = outcome || (isNew(notification) ? 'new' : null);
            return (
              <article key={notification.id ?? index} role={actionable ? 'button' : undefined} tabIndex={actionable ? 0 : undefined} onClick={actionable ? () => openNotification(notification) : undefined} onKeyDown={actionable ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openNotification(notification); } } : undefined} className={`relative overflow-hidden rounded-xl border border-slate-200 px-4 py-3 transition-colors ${actionable ? 'cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 focus:outline-none focus:ring-2 focus:ring-blue-400' : ''}`}>
                {ribbon && <span className={`pointer-events-none absolute right-[-34px] top-[18px] w-32 rotate-45 py-1 text-center text-xs font-bold uppercase tracking-wide text-white shadow-sm ${ribbon === 'approved' ? 'bg-green-600' : ribbon === 'rejected' ? 'bg-red-600' : 'bg-blue-600'}`}>{ribbon}</span>}
                <div className="flex flex-wrap items-center gap-2">
                  {notification.branch && <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">{String(notification.branch).trim().toUpperCase()}</span>}
                  <h2 className="font-semibold text-slate-900">{titleFor(notification)}</h2>
                  {!ribbon && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status === 'Pending' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>{status}</span>}
                </div>
                <div className="mt-1 flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
                  {messageFor(notification) && <p className="min-w-0 flex-1 break-words text-sm text-slate-600">{messageFor(notification)}</p>}
                  <time className={`ml-auto shrink-0 text-right text-xs text-slate-500 ${ribbon ? 'pr-14' : ''}`}>{formatTime(notification.time || notification.created_at || notification.created)}</time>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && hasMore && <div className="mt-5 text-center"><button type="button" onClick={() => loadNotifications(notifications.length)} disabled={loadingMore} className="rounded-lg border border-blue-300 px-5 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50">{loadingMore ? 'Loading...' : 'Load 50 more'}</button></div>}
    </div>
  );
};

export default NotificationsPage;
